<template>
  <div class="flex gap-6 items-start">
    <!-- Sommaire -->
    <nav class="w-56 flex-shrink-0 sticky top-0">
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">
          Endpoints
        </p>
        <ul class="space-y-0.5">
          <li v-for="ep in API_ENDPOINTS" :key="ep.id">
            <button
              @click="select(ep)"
              :class="[
                'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                selected.id === ep.id
                  ? 'bg-[#8C0004] bg-opacity-10 text-[#8C0004] font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              ]"
            >
              {{ ep.title }}
            </button>
          </li>
        </ul>

        <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-2 mt-3 border-t border-gray-100 pt-4">
          Guides
        </p>
        <ul class="space-y-0.5">
          <li>
            <button
              @click="tab = 'auth'"
              :class="['w-full text-left px-3 py-2 rounded-lg text-sm', tab === 'auth' ? 'bg-gray-100 font-semibold' : 'text-gray-700 hover:bg-gray-50']"
            >
              Authentification
            </button>
          </li>
          <li>
            <button
              @click="tab = 'webhooks'"
              :class="['w-full text-left px-3 py-2 rounded-lg text-sm', tab === 'webhooks' ? 'bg-gray-100 font-semibold' : 'text-gray-700 hover:bg-gray-50']"
            >
              Webhooks &amp; signature
            </button>
          </li>
          <li>
            <button
              @click="tab = 'sandbox'"
              :class="['w-full text-left px-3 py-2 rounded-lg text-sm', tab === 'sandbox' ? 'bg-gray-100 font-semibold' : 'text-gray-700 hover:bg-gray-50']"
            >
              Bac à sable
            </button>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Contenu -->
    <div class="flex-1 min-w-0 space-y-6">
      <!-- Guide authentification -->
      <section v-if="tab === 'auth'" class="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
        <h3 class="text-lg font-bold text-gray-900">Authentification</h3>
        <p class="text-sm text-gray-600">
          Chaque requête porte votre clé API dans l'en-tête <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs">Authorization</code>.
          Une clé <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs">yk_test_</code> ne fonctionne que sur l'URL sandbox,
          une clé <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs">yk_live_</code> que sur l'URL de production.
        </p>
        <pre class="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto">curl {{ baseUrl }}/me \
  -H "Authorization: Bearer VOTRE_CLE"</pre>
        <div class="grid sm:grid-cols-2 gap-4 text-sm">
          <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p class="font-semibold text-amber-900 mb-1">Sandbox (test)</p>
            <code class="text-xs break-all text-amber-800">{{ TEST_BASE }}</code>
          </div>
          <div class="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <p class="font-semibold text-emerald-900 mb-1">Production (live)</p>
            <code class="text-xs break-all text-emerald-800">{{ LIVE_BASE }}</code>
          </div>
        </div>
        <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700">
          <p class="font-semibold text-gray-900 mb-1">Idempotence</p>
          <p>
            Les créations (livraison, paiement) exigent un en-tête
            <code class="bg-white px-1.5 py-0.5 rounded text-xs border">Idempotency-Key</code> unique.
            Rejouer la même clé avec le même corps renvoie la réponse d'origine au lieu de créer un doublon :
            vous pouvez donc réessayer sans risque après un timeout réseau.
          </p>
        </div>
      </section>

      <!-- Guide webhooks -->
      <section v-else-if="tab === 'webhooks'" class="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
        <h3 class="text-lg font-bold text-gray-900">Webhooks &amp; vérification de signature</h3>
        <p class="text-sm text-gray-600">
          Chaque événement est envoyé en POST JSON avec l'en-tête
          <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs">X-Youdego-Signature: t=&lt;horodatage&gt;,v1=&lt;hmac&gt;</code>.
          Le HMAC est calculé en SHA-256 sur <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{{ '`${t}.${corpsBrut}`' }}</code>
          avec le secret de l'endpoint. Vérifiez-le toujours avant de traiter l'événement.
        </p>
        <pre class="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto">// Node.js (Express)
const crypto = require('crypto')

app.post('/webhooks/youdego',
  express.raw({ type: 'application/json' }),
  (req, res) => {
    const header = req.headers['x-youdego-signature'] || ''
    const parts = Object.fromEntries(header.split(',').map(p => p.split('=')))

    const expected = crypto
      .createHmac('sha256', process.env.YOUDEGO_WEBHOOK_SECRET)
      .update(`${parts.t}.${req.body}`)
      .digest('hex')

    if (!crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(parts.v1))) {
      return res.status(400).send('signature invalide')
    }

    const event = JSON.parse(req.body)
    // event.id sert d'identifiant d'idempotence côté réception
    console.log(event.type, event.data)

    res.sendStatus(200) // répondez 2xx rapidement
  })</pre>
        <div>
          <h4 class="font-semibold text-gray-900 text-sm mb-3">Catalogue des événements</h4>
          <div class="border border-gray-200 rounded-lg divide-y divide-gray-200">
            <div v-for="evt in EVENT_TYPES" :key="evt" class="p-3">
              <code class="text-sm text-[#8C0004] font-medium">{{ evt }}</code>
              <p class="text-xs text-gray-600 mt-0.5">{{ EVENT_DESCRIPTIONS[evt] }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Guide sandbox -->
      <section v-else-if="tab === 'sandbox'" class="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
        <h3 class="text-lg font-bold text-gray-900">Bac à sable</h3>
        <p class="text-sm text-gray-600">
          Avec une clé de test, aucun vrai livreur n'est sollicité : un simulateur fait avancer
          automatiquement vos livraisons (acceptation en ~10 s, puis ramassage et livraison), et règle
          vos paiements en ~15 s. Vos webhooks sont appelés exactement comme en production.
        </p>

        <div>
          <h4 class="font-semibold text-gray-900 text-sm mb-3">Provoquer un cas d'échec</h4>
          <p class="text-sm text-gray-600 mb-3">
            Ajoutez un objet <code class="bg-gray-100 px-1.5 py-0.5 rounded text-xs">sandbox</code> à la création :
          </p>
          <pre class="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto">{ "…": "…", "sandbox": { "scenario": "request_failed" } }</pre>
          <div class="border border-gray-200 rounded-lg divide-y divide-gray-200 mt-3">
            <div v-for="s in SANDBOX_SCENARIOS" :key="s.scenario" class="p-3">
              <code class="text-sm text-[#8C0004] font-medium">{{ s.scenario }}</code>
              <p class="text-xs text-gray-600 mt-0.5">{{ s.effect }}</p>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700">
          <p class="font-semibold text-gray-900 mb-1">Paiement en échec</p>
          <p>Un montant se terminant par <strong>99</strong> (ex : 1099) échoue volontairement.</p>
        </div>
      </section>

      <!-- Endpoint sélectionné + console -->
      <template v-else>
        <section class="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div class="flex items-center gap-3 mb-3">
            <span
              :class="[
                'px-2.5 py-1 rounded text-xs font-bold',
                selected.method === 'GET' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'
              ]"
            >
              {{ selected.method }}
            </span>
            <code class="text-sm font-mono text-gray-900">{{ selected.path }}</code>
            <span
              v-if="selected.idempotent"
              class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium"
            >
              Idempotency-Key requis
            </span>
          </div>
          <h3 class="text-lg font-bold text-gray-900 mb-1">{{ selected.title }}</h3>
          <p class="text-sm text-gray-600">{{ selected.description }}</p>

          <!-- Paramètres de chemin / requête / corps -->
          <div v-for="group in fieldGroups" :key="group.title" class="mt-5">
            <h4 class="text-sm font-semibold text-gray-900 mb-2">{{ group.title }}</h4>
            <div class="border border-gray-200 rounded-lg divide-y divide-gray-200">
              <div v-for="f in group.fields" :key="f.name" class="p-3">
                <div class="flex items-baseline gap-2 flex-wrap">
                  <code class="text-xs font-semibold text-gray-900">{{ f.name }}</code>
                  <span class="text-[11px] text-gray-500 font-mono">{{ f.type }}</span>
                  <span
                    :class="[
                      'text-[11px] px-1.5 py-0.5 rounded font-medium',
                      f.required ? 'bg-red-50 text-[#8C0004]' : 'bg-gray-100 text-gray-500'
                    ]"
                  >
                    {{ f.required ? 'requis' : 'optionnel' }}
                  </span>
                  <span v-if="f.default" class="text-[11px] text-gray-500">
                    défaut : <code class="text-gray-700">{{ f.default }}</code>
                  </span>
                </div>
                <p class="text-xs text-gray-600 mt-1">{{ f.description }}</p>

                <!-- Valeurs acceptées -->
                <div v-if="f.values" class="mt-2 space-y-1 pl-3 border-l-2 border-gray-100">
                  <div v-for="v in f.values" :key="v.value" class="flex gap-2 items-baseline">
                    <code class="text-[11px] font-semibold text-[#8C0004] flex-shrink-0">{{ v.value }}</code>
                    <span class="text-[11px] text-gray-600">{{ v.description }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Réponses possibles -->
        <section class="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 class="font-semibold text-gray-900 mb-1">Réponses possibles</h3>
          <p class="text-xs text-gray-500 mb-4">
            Chaque erreur porte un <code class="bg-gray-100 px-1 py-0.5 rounded">code</code> stable :
            appuyez-vous dessus plutôt que sur le message, qui peut évoluer.
          </p>

          <div class="border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
            <div v-for="(r, i) in selected.responses" :key="`${r.status}-${r.code || i}`">
              <button
                @click="toggleResponse(i)"
                class="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors"
              >
                <span
                  :class="['px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 w-11 text-center', statusClass(r.status)]"
                >
                  {{ r.status }}
                </span>
                <span class="text-sm font-medium text-gray-900 flex-shrink-0">{{ r.label }}</span>
                <code v-if="r.code" class="text-[11px] text-gray-500">{{ r.code }}</code>
                <Icon
                  :icon="openResponses[i] ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'"
                  class="w-4 h-4 text-gray-400 ml-auto flex-shrink-0"
                />
              </button>
              <div v-if="openResponses[i]" class="px-3 pb-3">
                <p class="text-xs text-gray-600 mb-2">{{ r.description }}</p>
                <pre class="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs overflow-x-auto">{{ r.example }}</pre>
              </div>
            </div>
          </div>
        </section>

        <!-- Console d'essai -->
        <section class="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="font-semibold text-gray-900">Essayer</h3>
              <p class="text-xs text-gray-500">
                Requête envoyée à <code class="bg-gray-100 px-1 py-0.5 rounded">{{ baseUrl }}</code>
              </p>
            </div>
            <span
              v-if="modeStore.isLive"
              class="px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-semibold"
            >
              Mode live — requêtes réelles
            </span>
          </div>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Clé API</label>
              <input
                v-model="apiKey"
                type="password"
                :placeholder="modeStore.isTest ? 'yk_test_…' : 'yk_live_…'"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
              />
              <p class="text-xs text-gray-500 mt-1.5">
                Collez une clé créée dans
                <router-link to="/api-keys" class="text-[#8C0004] hover:underline">Clés API</router-link>.
                Elle reste dans votre navigateur, le temps de la session.
              </p>
            </div>

            <div v-for="p in selected.pathParams || []" :key="p.name">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ p.name }} <span class="text-red-600">*</span>
              </label>
              <input
                v-model="pathValues[p.name]"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
              />
              <p class="text-xs text-gray-500 mt-1">{{ p.description }}</p>
            </div>

            <div v-if="selected.sampleBody">
              <label class="block text-sm font-medium text-gray-700 mb-2">Corps de la requête</label>
              <textarea
                v-model="body"
                rows="12"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
              />
            </div>

            <button
              @click="send"
              :disabled="!apiKey || sending"
              class="px-5 py-2.5 bg-[#8C0004] text-white rounded-lg text-sm font-medium hover:bg-red-900 disabled:opacity-50 flex items-center gap-2"
            >
              <Icon v-if="sending" icon="svg-spinners:ring-resize" class="w-4 h-4" />
              {{ sending ? 'Envoi...' : 'Envoyer la requête' }}
            </button>
          </div>

          <!-- Réponse -->
          <div v-if="response" class="mt-6">
            <div class="flex items-center gap-2 mb-2">
              <span class="text-sm font-medium text-gray-700">Réponse</span>
              <span
                :class="[
                  'px-2 py-0.5 rounded text-xs font-bold',
                  response.ok ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                ]"
              >
                HTTP {{ response.status }}
              </span>
            </div>
            <pre class="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto max-h-96">{{ response.body }}</pre>
          </div>

          <!-- Exemple de réponse (cas nominal) -->
          <div v-else class="mt-6">
            <p class="text-sm font-medium text-gray-700 mb-2">
              Exemple de réponse — {{ successResponse?.label }}
            </p>
            <pre class="bg-gray-50 border border-gray-200 text-gray-700 rounded-lg p-4 text-xs overflow-x-auto">{{ successResponse?.example }}</pre>
          </div>
        </section>

        <!-- curl équivalent -->
        <section class="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 class="font-semibold text-gray-900 mb-3">Équivalent curl</h3>
          <pre class="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto">{{ curlSnippet }}</pre>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Icon } from '@iconify/vue'
import { apiBaseUrlFor, LIVE_API_URL, TEST_API_URL } from '../services/api'
import { useModeStore } from '../store/mode'
import { API_ENDPOINTS, SANDBOX_SCENARIOS, ApiEndpoint, FieldSpec } from '../services/apiSpec'
import { EVENT_TYPES, EVENT_DESCRIPTIONS } from '../services/events'

const modeStore = useModeStore()

const LIVE_BASE = LIVE_API_URL + '/v1'
const TEST_BASE = TEST_API_URL + '/v1'

const tab = ref<'endpoint' | 'auth' | 'webhooks' | 'sandbox'>('endpoint')
const selected = ref<ApiEndpoint>(API_ENDPOINTS[0])
const apiKey = ref(sessionStorage.getItem('partnerConsoleKey') || '')
const body = ref(API_ENDPOINTS[0].sampleBody || '')
const pathValues = ref<Record<string, string>>({})
const sending = ref(false)
const response = ref<{ ok: boolean; status: number; body: string } | null>(null)

const baseUrl = computed(() => apiBaseUrlFor(modeStore.mode))

// Réponses dépliables : les erreurs restent repliées, le cas nominal ouvert
const openResponses = ref<Record<number, boolean>>({ 0: true })

function toggleResponse(index: number) {
  openResponses.value = { ...openResponses.value, [index]: !openResponses.value[index] }
}

const successResponse = computed(() =>
  selected.value.responses.find((r) => r.status < 300)
)

const fieldGroups = computed(() => {
  const groups: { title: string; fields: FieldSpec[] }[] = []
  if (selected.value.pathParams?.length) {
    groups.push({ title: 'Paramètres de chemin', fields: selected.value.pathParams })
  }
  if (selected.value.queryParams?.length) {
    groups.push({ title: 'Paramètres de requête', fields: selected.value.queryParams })
  }
  if (selected.value.bodyFields?.length) {
    groups.push({ title: 'Corps de la requête', fields: selected.value.bodyFields })
  }
  return groups
})

function statusClass(status: number): string {
  if (status < 300) return 'bg-emerald-100 text-emerald-800'
  if (status < 500) return 'bg-amber-100 text-amber-800'
  return 'bg-red-100 text-red-800'
}

// La clé n'est jamais persistée durablement : sessionStorage seulement.
watch(apiKey, (v) => sessionStorage.setItem('partnerConsoleKey', v))

// Changer d'environnement invalide la clé saisie (une clé test ne vaut pas en live)
watch(
  () => modeStore.mode,
  () => {
    apiKey.value = ''
    response.value = null
  }
)

const resolvedPath = computed(() => {
  let path = selected.value.path
  for (const [name, value] of Object.entries(pathValues.value)) {
    path = path.replace(`:${name}`, value || `:${name}`)
  }
  return path
})

const curlSnippet = computed(() => {
  const lines = [`curl -X ${selected.value.method} ${baseUrl.value}${resolvedPath.value} \\`]
  lines.push(`  -H "Authorization: Bearer ${apiKey.value || 'VOTRE_CLE'}" \\`)
  if (selected.value.sampleBody) {
    lines.push(`  -H "Content-Type: application/json" \\`)
    if (selected.value.idempotent) {
      lines.push(`  -H "Idempotency-Key: $(uuidgen)" \\`)
    }
    lines.push(`  -d '${(body.value || '').replace(/\n\s*/g, ' ')}'`)
  } else {
    lines[lines.length - 1] = lines[lines.length - 1].replace(/ \\$/, '')
  }
  return lines.join('\n')
})

function select(ep: ApiEndpoint) {
  tab.value = 'endpoint'
  selected.value = ep
  body.value = ep.sampleBody || ''
  pathValues.value = {}
  response.value = null
  // Repartir avec le seul cas nominal déplié
  openResponses.value = { 0: true }
}

async function send() {
  sending.value = true
  response.value = null

  try {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${apiKey.value}`,
    }
    const init: RequestInit = { method: selected.value.method, headers }

    if (selected.value.method === 'POST') {
      headers['Content-Type'] = 'application/json'
      if (selected.value.idempotent) {
        // Une clé neuve à chaque essai : la console sert à explorer, pas à rejouer
        headers['Idempotency-Key'] = `console-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      }
      if (selected.value.sampleBody) {
        init.body = body.value
      }
    }

    const res = await fetch(`${baseUrl.value}${resolvedPath.value}`, init)
    const text = await res.text()

    let pretty = text
    try {
      pretty = JSON.stringify(JSON.parse(text), null, 2)
    } catch {
      /* réponse non-JSON : afficher telle quelle */
    }

    response.value = { ok: res.ok, status: res.status, body: pretty }
  } catch (err: any) {
    response.value = {
      ok: false,
      status: 0,
      body: `Requête impossible : ${err.message}\n\nVérifiez votre connexion.`,
    }
  } finally {
    sending.value = false
  }
}
</script>
